import React from 'react'
import { SpaceIcon } from '@components/SpaceIcon'
import { ImageVariants } from '@components/UploadImage/useImageSource'
import { useEthToUsdFormatted } from '@components/Web3/useEthPrice'
import { Box, Icon, Stack, Text } from '@ui'
import { formatUnitsToFixedLength } from 'hooks/useBalance'
import { useBotMetadataQuery } from 'hooks/useBotMetadataQuery'
import { useBotReviews } from 'hooks/useBotReviews'
import { useBotStake } from 'hooks/useBotStake'
import { useBotTips } from 'hooks/useBotTips'
import { useDevice } from 'hooks/useDevice'
import { useMobile } from 'hooks/useMobile'
import { type Address, SpaceIdFromSpaceAddress, useBotInstalledIn } from 'use-towns-client'

const numberFormatter = new Intl.NumberFormat('en-US')

const getHighlightSpaces = (spaceAddressInstalledIn: string[]) => {
    return spaceAddressInstalledIn.slice(0, 3)
}

export const InstallBotInfoSection = ({ appAddress }: { appAddress: Address }) => {
    const isMobile = useMobile()

    const { data: installedIn } = useBotInstalledIn(appAddress)
    const { data: botMetadata, isLoading: isBotMetadataLoading } = useBotMetadataQuery(appAddress)
    const { data: reviewData } = useBotReviews(appAddress)
    const averageRating = reviewData?.summary?.averageRating ?? 0
    const totalReviews = reviewData?.summary?.totalFeedback ?? 0

    const { totalTips } = useBotTips(appAddress)
    const { totalStake } = useBotStake(appAddress)

    const tipsInUsd = useEthToUsdFormatted({ ethAmount: totalTips, noDecimals: true })?.replace(
        '$',
        '',
    )

    const totalUsers = botMetadata?.totalUsers ?? 0
    const totalUsersFormatted = numberFormatter.format(totalUsers)
    const installedSpacesSpaceId =
        installedIn?.map((spaceId) => SpaceIdFromSpaceAddress(spaceId)) ?? []
    const highlightSpaces = getHighlightSpaces(installedSpacesSpaceId)

    const stakeFormatted = totalStake ? formatUnitsToFixedLength(totalStake, 18, 4) : 0

    return (
        <Stack gap="md">
            <Stack
                horizontal
                rounded
                centerContent
                alignSelf="start"
                paddingX="none"
                paddingY="xs"
                alignItems="center"
                border="transparent"
                minWidth={isMobile ? '100%' : 'none'}
                maxWidth={isMobile ? '100%' : '600'}
            >
                <InfoSectionItem
                    title="Users"
                    centerContent={
                        isBotMetadataLoading ? (
                            <></>
                        ) : (
                            <Text
                                size="lg"
                                fontWeight="strong"
                                textAlign="center"
                                style={{ lineHeight: 0.9 }}
                            >
                                {totalUsersFormatted}
                            </Text>
                        )
                    }
                    bottomContent={
                        <Stack horizontal gap="xs" alignItems="center" justifyContent="center">
                            {highlightSpaces?.map((spaceId) => (
                                <SpaceIcon
                                    variant={ImageVariants.thumbnail100}
                                    overrideBorderRadius="xs"
                                    width="x2"
                                    height="x2"
                                    key={spaceId}
                                    spaceId={spaceId}
                                    fadeIn={false}
                                    firstLetterOfSpaceName=""
                                />
                            ))}
                        </Stack>
                    }
                />

                {totalReviews > 0 ? (
                    <>
                        <Delimiter />
                        <InfoSectionItem
                            title="Reviews"
                            centerContent={
                                <Stack
                                    horizontal
                                    gap="xs"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Text
                                        size="lg"
                                        fontWeight="strong"
                                        textAlign="center"
                                        style={{ lineHeight: 0.9 }}
                                    >
                                        {averageRating.toFixed(1)}
                                    </Text>
                                </Stack>
                            }
                            bottomContent={
                                <Text size="sm" color="gray2" textAlign="center">
                                    {totalReviews} {totalReviews === 1 ? 'Review' : 'Reviews'}
                                </Text>
                            }
                        />
                    </>
                ) : (
                    <></>
                )}
                {totalTips ? (
                    <>
                        <Delimiter />
                        <InfoSectionItem
                            title="Tips"
                            centerContent={
                                <Stack
                                    horizontal
                                    gap="xs"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Icon type="dollarFilled" size="square_md" color="cta1" />
                                    <Text
                                        size="lg"
                                        fontWeight="strong"
                                        textAlign="center"
                                        style={{ lineHeight: 0.9 }}
                                    >
                                        {tipsInUsd}
                                    </Text>
                                </Stack>
                            }
                            bottomContent={
                                <Text size="sm" color="gray2" textAlign="center">
                                    Sent
                                </Text>
                            }
                        />
                    </>
                ) : (
                    <></>
                )}
                {totalStake ? (
                    <>
                        <Delimiter />
                        <InfoSectionItem
                            title="Stake"
                            centerContent={
                                <Text
                                    size="lg"
                                    fontWeight="strong"
                                    textAlign="center"
                                    style={{ lineHeight: 0.9 }}
                                >
                                    {stakeFormatted}
                                </Text>
                            }
                            bottomContent={
                                <Text size="sm" color="gray2" textAlign="center">
                                    ETH
                                </Text>
                            }
                        />
                    </>
                ) : (
                    <></>
                )}
            </Stack>
        </Stack>
    )
}

const Delimiter = () => {
    return (
        <Box paddingY="sm" width="1" height="100">
            <Box grow borderRight="transparent" width="1" />
        </Box>
    )
}

interface InfoSectionItemProps {
    title: string
    centerContent: React.ReactNode
    bottomContent?: React.ReactNode
    onClick?: () => void
    hoverable?: boolean
    style?: React.CSSProperties
}

export const InfoSectionItem = ({
    title,
    centerContent,
    bottomContent,
    onClick,
    hoverable = false,
    style,
}: InfoSectionItemProps) => {
    const { isTouch } = useDevice()

    return (
        <Stack
            grow
            centerContent
            gap
            minWidth={{ desktop: '150', mobile: 'none' }}
            height="100"
            position="relative"
            style={{ flex: 1, ...style }}
            cursor={hoverable ? 'pointer' : 'default'}
            hoverable={hoverable}
            {...(onClick ? { [isTouch ? 'onMouseDown' : 'onClick']: onClick } : {})}
        >
            <Box justifyContent="end" height="x1">
                <Text
                    size="sm"
                    color="gray2"
                    textTransform="uppercase"
                    fontWeight="strong"
                    textAlign="center"
                    style={{
                        letterSpacing: -0.8,
                    }}
                >
                    {title}
                </Text>
            </Box>

            <Box height="x2" width="100%" justifyContent="center" alignItems="center">
                {centerContent}
            </Box>

            <Box centerContent height="x1" justifyContent="start">
                {bottomContent}
            </Box>
        </Stack>
    )
}
